import { z } from 'zod';
/**
 * Contrats de l'OUTIL EXIF & des TAGS (T4, XPHOTO-04/-05/-06/-24). Source unique
 * front + back.
 *
 * - `tagSchema` / `createTagSchema` / `renameTagSchema` : taxonomie plate en base
 *   (XPHOTO-05), source de vérité des mots-clés (anglais).
 * - `assetTagSchema`         : ajout d'un tag manuel sur un asset.
 * - `exifAnalysisSchema`     : rapport d'analyse (présence par champ + divergence
 *   publication DB↔fichier), XPHOTO-24, en LECTURE (dérivé d'`exif_snapshot`).
 * - `exifEraseRequestSchema` : effacement sélectif (XPHOTO-04) — GPS toujours
 *   effacé, groupes de champs conservables au choix, option `_original`.
 * - `exifPublishRequestSchema` : publication des tags validés en IPTC/XMP
 *   (XPHOTO-06), option `_original`, dry-run.
 */
// ---------------------------------------------------------------------------
// Tags (XPHOTO-05)
// ---------------------------------------------------------------------------
/** Longueur max d'une étiquette de tag (garde-fou contrat). */
export const TAG_LABEL_MAX = 128;
/**
 * Longueur max d'une DESCRIPTION de tag (définition libre optionnelle, guide l'IA de
 * tagging). Garde-fou contrat ; la description est facultative (NULL = aucune).
 */
export const TAG_DESCRIPTION_MAX = 500;
/**
 * Description d'un tag saisie par l'utilisateur : chaîne TRIMÉE et bornée.
 *
 * ⚠ AUCUN `.transform()` ici : ce schéma est utilisé comme CORPS de route (PATCH tag)
 * et passe donc par `z.toJSONSchema()` (génération du schéma AJV Fastify au démarrage).
 * Zod v4 LÈVE « Transforms cannot be represented in JSON Schema » sur un `.transform`
 * → l'API plantait au boot. La normalisation « vide → efface (null) » est faite dans le
 * HANDLER de route (pas dans le schéma). Piège connu (cf. `statesFilterSchema`).
 */
export const tagDescriptionSchema = z.string().trim().max(TAG_DESCRIPTION_MAX);
/** Un tag de la taxonomie (anglais, unique par bibliothèque). */
export const tagSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    createdAt: z.string().datetime({ offset: true }),
});
/** Corps de création/renommage d'un tag : étiquette non vide, bornée. */
export const createTagSchema = z.object({
    label: z.string().trim().min(1).max(TAG_LABEL_MAX),
});
/**
 * Corps de `PATCH /api/libraries/:id/tags/:tagId`. AU MOINS un champ à mettre à jour :
 *
 * - `label`       : renomme le tag (étiquette non vide, bornée) — comportement historique.
 * - `description` : (dé)fini la définition libre du tag (`null` ou vide → efface).
 *
 * Les deux sont optionnels mais au moins un doit être fourni (`refine`) ; sinon 400.
 * Un `PATCH { label }` seul se comporte comme l'ancien renommage. `description: null`
 * (ou chaîne vide) EFFACE la description.
 */
export const updateTagSchema = z
    .object({
    label: z.string().trim().min(1).max(TAG_LABEL_MAX).optional(),
    // `.nullable()` : `description: null` (ou chaîne vide, normalisée côté route) EFFACE.
    description: tagDescriptionSchema.nullable().optional(),
})
    .refine((body) => body.label !== undefined || body.description !== undefined, {
    message: 'Au moins un champ (label ou description) doit être fourni',
});
/** Corps d'ajout d'un tag manuel sur un asset (par étiquette ; créé si absent). */
export const assetTagRequestSchema = z.object({
    label: z.string().trim().min(1).max(TAG_LABEL_MAX),
});
// ---------------------------------------------------------------------------
// Analyse EXIF (XPHOTO-24)
// ---------------------------------------------------------------------------
/**
 * Champs EXIF suivis par l'analyse (présence par champ). Allow-list stable :
 * l'API teste l'existence de CES clés dans `exif_snapshot` (jamais d'entrée brute
 * dans le SQL). Alignés sur la sortie ExifTool (`-json -n`).
 */
export const EXIF_TRACKED_FIELDS = [
    'DateTimeOriginal',
    'CreateDate',
    'GPSLatitude',
    'Make',
    'Model',
    'LensModel',
    'Artist',
    'Copyright',
    'Software',
    'Orientation',
    'Keywords',
    'Subject',
];
/**
 * Sous-ensemble des champs suivis qui sont des DATES (valeur « AAAA:MM:JJ HH:MM:SS »).
 * Traitement spécial : un conteneur QuickTime (MP4/MOV) porte des dates STRUCTURELLES
 * dans ses en-têtes (`mvhd`/`tkhd`/`mdhd`) qu'ExifTool ne peut PAS supprimer — un
 * effacement les remet à `0000:00:00 00:00:00` (« zérotées ») sans retirer la clé. On
 * considère alors le champ ABSENT (l'effacement a réussi, la valeur est détruite).
 */
export const EXIF_DATE_FIELDS = ['DateTimeOriginal', 'CreateDate'];
/** Vrai si le champ suivi est une date (cf. dates QuickTime zérotées non supprimables). */
export function isExifDateField(field) {
    return EXIF_DATE_FIELDS.includes(field);
}
/**
 * Préfixe d'une date EXIF « zérotée » (`0000:00:00 00:00:00`) — date structurelle
 * QuickTime remise à zéro par un effacement. Le SQL bâtit le motif LIKE `0000:00:00%`
 * (lié en paramètre) pour NE PAS compter le champ « présent » après effacement.
 */
export const ZEROED_EXIF_DATE_PREFIX = '0000:00:00';
/** Présence d'un champ EXIF : nombre d'assets présents le portant. */
export const exifFieldStatSchema = z.object({
    key: z.enum(EXIF_TRACKED_FIELDS),
    present: z.number().int().nonnegative(),
});
/**
 * Query de `GET /api/libraries/:id/exif/analysis`. `folder` = sous-arbre optionnel
 * (préfixe `rel_path`, MÊME sémantique que la portée d'effacement/les facettes
 * dossier) → l'analyse (compteurs par champ + divergence de publication) ne reflète
 * QUE ce dossier. Absent ⇒ toute la bibliothèque (comportement inchangé).
 */
export const exifAnalysisQuerySchema = z.object({
    folder: z.string().min(1).optional(),
});
/**
 * Rapport d'analyse EXIF d'une bibliothèque (XPHOTO-24). `fields` = présence par
 * champ ; `publish` = divergence DB↔fichier des mots-clés (source de vérité = DB).
 */
export const exifAnalysisSchema = z.object({
    /** Assets présents (non absents) analysés. */
    total: z.number().int().nonnegative(),
    fields: z.array(exifFieldStatSchema),
    publish: z.object({
        /** Assets portant au moins un tag validé en base. */
        taggedAssets: z.number().int().nonnegative(),
        /** Assets dont les mots-clés du fichier correspondent déjà aux tags validés. */
        inSync: z.number().int().nonnegative(),
        /** Assets dont le fichier diverge → seraient mis à jour par une publication. */
        outOfSync: z.number().int().nonnegative(),
    }),
});
// ---------------------------------------------------------------------------
// Effacement sélectif (XPHOTO-04)
// ---------------------------------------------------------------------------
/**
 * Groupes de champs CONSERVABLES lors d'un effacement. Tout le reste est effacé ;
 * le GPS est TOUJOURS effacé (jamais conservable — décision SPEC §5).
 */
export const exifKeepFieldSchema = z.enum(['date', 'orientation', 'copyright']);
/**
 * Corps de `POST /api/libraries/:id/exif/erase`. DEUX modes exclusifs :
 *
 * - **Effacement CIBLÉ par champ** (`fields` présent, non vide) : efface UNIQUEMENT
 *   les champs listés (sous-ensemble de `EXIF_TRACKED_FIELDS`, allow-list), en masse
 *   sur toute la bibliothèque (ou le sous-arbre `folder`). Le reste est INTACT. C'est
 *   le mode central du « Nettoyage EXIF » (retour utilisateur : « effacer un tag EXIF
 *   choisi sur toutes les photos en batch »). `keep` est IGNORÉ dans ce mode.
 * - **Effacement GLOBAL avec conservation** (`fields` absent/vide) : `-all=` puis
 *   restauration des groupes `keep` à préserver (déduplication implicite côté service) ;
 *   le GPS est TOUJOURS effacé (jamais conservable — décision SPEC §5).
 *
 * `folder` = sous-arbre optionnel (préfixe `rel_path`). `keepOriginal` (défaut vrai)
 * conserve un `_original`. `dryRun` simule sans écrire.
 */
export const exifEraseRequestSchema = z.object({
    keep: z.array(exifKeepFieldSchema).default([]),
    /** Champs à effacer en CIBLÉ (allow-list) ; s'il est fourni, ce mode prime sur `keep`. */
    fields: z.array(z.enum(EXIF_TRACKED_FIELDS)).nonempty().optional(),
    folder: z.string().min(1).optional(),
    keepOriginal: z.boolean().default(true),
    dryRun: z.boolean().default(false),
});
// ---------------------------------------------------------------------------
// Publication des tags (XPHOTO-06)
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/libraries/:id/exif/publish`. Exporte les tags VALIDÉS en
 * IPTC:Keywords + XMP dc:subject sur les fichiers divergents. `folder` = sous-arbre
 * optionnel. `keepOriginal` (défaut vrai) conserve un `_original`.
 */
export const exifPublishRequestSchema = z.object({
    folder: z.string().min(1).optional(),
    keepOriginal: z.boolean().default(true),
    dryRun: z.boolean().default(false),
});
/**
 * Ventilation d'un job EXIF (publication/effacement). Renseignée dans les logs et
 * (T4b) exposée à l'UI. Chaque asset traité tombe dans une catégorie.
 */
export const exifJobCountsSchema = z.object({
    total: z.number().int().nonnegative(),
    written: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Nettoyage des sauvegardes `_original` d'ExifTool (sûreté, XPHOTO-04)
// ---------------------------------------------------------------------------
/**
 * Un fichier de sauvegarde `<nom>_original` créé par ExifTool (quand `keepOriginal`
 * est activé). `relPath` est POSIX, relatif à la racine bibliothèque ; `sourceExists`
 * dit si le fichier source correspondant (nom SANS le suffixe `_original`) existe
 * encore — HEURISTIQUE DE SÛRETÉ : seuls les `_original` dont la source existe sont
 * considérés comme de vraies sauvegardes supprimables (jamais un fichier légitimement
 * nommé « …_original »).
 */
export const originalBackupSchema = z.object({
    relPath: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    sourceExists: z.boolean(),
});
/**
 * Query de `POST /api/libraries/:id/exif/originals/scan` (parcours FS, LECTURE).
 * `folder` = sous-arbre optionnel (même sémantique que la portée d'effacement).
 */
export const originalsScanRequestSchema = z.object({
    folder: z.string().min(1).optional(),
});
/** Longueur max de la liste d'exemples renvoyée au client (garde-fou UI). */
export const ORIGINALS_SAMPLE_MAX = 100;
/**
 * Résultat d'un parcours des sauvegardes `_original` : nombre TOTAL de sauvegardes
 * (source existante), taille cumulée, et un ÉCHANTILLON borné pour l'affichage.
 * `total`/`totalBytes` couvrent TOUT (pas seulement l'échantillon).
 */
export const originalsScanResultSchema = z.object({
    total: z.number().int().nonnegative(),
    totalBytes: z.number().int().nonnegative(),
    /** Échantillon (≤ `ORIGINALS_SAMPLE_MAX`) des sauvegardes trouvées, pour l'UI. */
    sample: z.array(originalBackupSchema),
});
/**
 * Corps de `POST /api/libraries/:id/exif/originals/cleanup` (SUPPRESSION, ÉCRITURE).
 * Ne supprime QUE des `*_original` dont la source existe ; `folder` = sous-arbre
 * optionnel. `dryRun` = compte sans supprimer.
 */
export const originalsCleanupRequestSchema = z.object({
    folder: z.string().min(1).optional(),
    dryRun: z.boolean().default(false),
});
/** Ventilation d'un nettoyage `_original` : trouvés / supprimés / octets libérés / échecs. */
export const originalsCleanupResultSchema = z.object({
    total: z.number().int().nonnegative(),
    deleted: z.number().int().nonnegative(),
    freedBytes: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
//# sourceMappingURL=exif.js.map